<script setup lang="ts">
import { SUPPORT_EMAIL } from "@/scripts/constants";
</script>
<template>
  <div>
    <p>CHARGE CARD CARDMEMBER AGREEMENT</p>

    <p>
      THIS AGREEMENT INCLUDES AN ARBITRATION CLAUSE (EXHIBIT A). PLEASE READ IT
      CAREFULLY.
    </p>

    <p>
      UNLESS YOU ARE A COVERED BORROWER UNDER THE MILITARY LENDING ACT AT THE
      TIME YOU OPEN YOUR ACCOUNT, THE ARBITRATION PROVISION WILL HAVE A
      SUBSTANTIAL IMPACT ON YOUR RIGHTS IN THE EVENT OF A DISPUTE WITH US. IF WE
      ELECT TO REQUIRE ARBITRATION OF A DISPUTE, YOU WILL NOT HAVE A RIGHT TO A
      JURY TRIAL OR A RIGHT TO BRING OR PARTICIPATE IN A CLASS ACTION. IF YOU DO
      NOT AGREE TO THE TERMS OF THE ARBITRATION CLAUSE, DO NOT APPLY FOR CARD
      AND/OR OPEN CARD ACCOUNT.
    </p>

    <p>
      This Charge Card Cardmember Agreement, together with the Disclosures above
      and the Arbitration Clause set forth under Exhibit A (collectively, this
      &ldquo;Agreement&rdquo;) contains the terms and conditions which govern
      the use of your Cloaked Pay charge card (&ldquo;Card&rdquo;) issued to you
      by Community Federal Savings Bank (the &ldquo;Bank&rdquo;). Cloaked Pay,
      LLC (&ldquo;Program Manager&rdquo;) is the company that provides various
      services to Bank in connection with the Card and Card Account. This
      Agreement and the Card and Card Account are made available to you through
      the website and/or mobile application of Program Manager (collectively,
      the &ldquo;App&rdquo;). By accepting and using the Card and/or Card
      Account, or by authorizing anyone else to use the Card or Card Account,
      you agree to be bound by the terms and conditions in this Agreement. If
      you do not wish to be bound to this Agreement, please cancel your Card and
      do not use it or let anyone else use your Card or Card Account within the
      30 days after you received the Card. The application you signed or
      otherwise submitted (including the federal and state notices), any
      temporary card and the card carrier the Bank sends with your Card are part
      of and are incorporated into this Agreement. Please read them in their
      entirety and keep them for your records. In addition to this Agreement,
      your Card is governed by Statements, our privacy policy, any brochure
      describing benefits provided by Card Network, all disclosures and
      materials provided to you before and when you applied for and/or opened
      your Card and Card Account and any future changes to the aforementioned
      documents.
    </p>

    <p>
      If any fee in the Fees table of the Disclosures is marked
      &ldquo;none&rdquo; or &ldquo;not applicable&rdquo;, the section of this
      Agreement that relates to that fee does not apply.
    </p>

    <p>
      Contact us if you have any questions about this Agreement or your Card or
      Card Account. You may contact us by calling +1(888) 857-5490 or write to
      us at {{ SUPPORT_EMAIL }}.
    </p>

    <p>
      Important Information About Procedures for Opening a New Card and Card
      Account
    </p>

    <p>
      To help the government fight the funding of terrorism and money laundering
      activities, Federal law requires all financial institutions to obtain,
      verify, and record information that identifies each person who obtains a
      Card and Card Account.
    </p>

    <p>
      What this means for you: When you apply for a Card and Card Account, we
      will ask for your name, address, date of birth, social security number or
      tax identification number, and other information that will allow us to
      identify you. We may also ask to see a copy of your driver's license or
      other identifying documents.
    </p>

    <h1>1. Definitions.</h1>

    <p>
      All capitalized terms used in this Agreement and not otherwise defined
      have the meanings set forth below.
    </p>

    <p>
      &ldquo;Authorized User&rdquo; means any person to whom you have granted
      permission to use a Card or other access credentials associated with your
      Card Account. Authorized Users may initiate transactions using the Card or
      Card Account, but they are not parties to this Agreement and are not
      liable for any amounts owed.
    </p>

    <p>
      &ldquo;Billing Cycle&rdquo; means the time period between billing
      statements and is used to manage your Card Account. Each Statement shows
      the closing date, which is the last day of the Billing Cycle.
    </p>

    <p>
      &ldquo;Card&rdquo; means one or more charge cards or other access devices,
      including your Card Account number or any virtual card, that we may issue
      to you or you may otherwise use to access your Card account (&ldquo;Card
      Account&rdquo;), and includes all renewals and substitutions of the same.
      All the terms of this Agreement also apply to the Card Account.
    </p>

    <p>
      &ldquo;Fees&rdquo; means charges imposed on your Card as set out in the
      Fees table of the Disclosures.
    </p>

    <p>
      &ldquo;Linked Account&ldquo; means the debit card account, deposit account
      (e.g., checking), or other account or card type issued by a U.S. financial
      institution that you link to your Card Account that enables you to pay
      amounts through App functionality and owed under this Agreement.
      Additional payment options, such as ACH transfers, may be available
      through the App, subject to Program Manager's terms and conditions.
    </p>

    <p>
      &ldquo;New Balance&rdquo; means the total of all unpaid Purchases,
      transactions, fees, charges, or any other amounts due under this Agreement
      as of the last day of the Billing Cycle. The New Balance includes any new
      transactions, fees or charges posted to the Account during the Billing
      Cycle and any amounts unpaid from prior Billing Cycles.
    </p>

    <p>
      &ldquo;Payment Due Date&rdquo; means the payment due date shown on your
      Statement.
    </p>

    <p>
      &ldquo;Purchase&rdquo; means the use of your Card or Card Account to buy
      or lease goods or services, including payment to government agencies
      (e.g., tax payments), service providers, and other permitted transactions
      as determined by us. This includes any associated fees or charges
      processed as part of the transaction.
    </p>

    <p>
      &ldquo;Spending Limit&rdquo; means the transactional (dollar) amount or
      limits per day that you are permitted to spend on through your Card
      Account.
    </p>

    <p>
      &ldquo;Statement&rdquo; means the periodic statement(s) we send to you in
      connection with your Card and Card Account.
    </p>

    <p>
      &ldquo;We,&rdquo; &ldquo;us,&rdquo; and &ldquo;our&rdquo; mean Community
      Federal Savings Bank, and its agents, authorized representatives,
      successors, assigns. For purposes of the Arbitration Clause only,
      &ldquo;we,&rdquo; &ldquo;us,&rdquo; and &ldquo;our&rdquo; also include
      Program Manager and other identified in the Arbitration Clause, as
      applicable.
    </p>

    <p>
      &ldquo;You&rdquo; and &ldquo;your&rdquo; mean each person who applied for
      and has been approved by us for the Card and/or Card Account, and who is
      legally responsible for all transactions and obligations under this
      Agreement.
    </p>

    <h1>2. Account Information and Contacting You</h1>

    <p>Card and Card Account Information.</p>

    <p>
      We need information about you to open and manage your Card and Card
      Account. This includes but is not limited to: (1) your legal name; (2) a
      valid mailing address; (3) your date of birth; (4) your Social Security
      number or other government identification number, and may also include (5)
      your telephone number(s); (6) your email address; and (7) your employment
      and income information. We may also require additional information such as
      photo identification, to verify your identity or comply with applicable
      law.
    </p>

    <p>Changes to Your Card Account.</p>

    <p>
      You provided certain personal information to us when you opened your Card
      and Card Account. You must tell us immediately when this information
      changes. We may ask you for or require you to provide additional
      information, supplemental information or both to verify any changes to
      information you have provided us. The supplemental information may affect
      your Spending Limit. A failure to provide the information we request or
      require may result in suspension or termination of your Card and Card
      Account. We may also restrict or close your Card and Card Account if we
      cannot verify your information.
    </p>

    <p>Contacting You.</p>

    <p>
      To the extent permitted by applicable law, you authorize us and consent to
      us and our Program Manager and each of our respective affiliates, agents,
      and contractors, and anyone to whom we may sell your Card and Card
      Account, to contact you to service your Card and Card Account or for
      collection purposes. You agree that these contacts are not unsolicited for
      purposes of state or federal law. You further agree that we, our
      affiliates, agents, and contractors (including, but not limited to,
      Program Manager) and anyone to whom we may sell your Card and Card Account
      may: (1) contact you in any way, including mail, email, calls, and texts,
      including a mobile, wireless, or similar device, even if you are charged
      by your provider, and using automated telephone equipment or pre-recorded
      messages; (2) contact you at any number that you have given us or any
      number we have for you in our records, even if that number is a wireless,
      cellular, or mobile number, is converted to a mobile/wireless number, or
      connects to any type of mobile/wireless device, and even if such telephone
      number is currently listed on a Do Not Call Registry; and (3) contact you
      at any email address you provide to us or any other person or company that
      provides any services in connection with this Agreement. We may monitor,
      tape or electronically record our telephone calls with you, including any
      calls with our customer service department, collection department and any
      other department. For the avoidance of doubt, you agree you will accept
      calls from us regarding your Card and Card Account. You understand these
      calls could be automatically dialed and a recorded message may be played.
      You agree to notify us immediately if you change your email address,
      mailing address, or phone number.
    </p>

    <h1>3. Card and Card Account Availability</h1>

    <p>
      The Card and Card Account are available to United States citizens or
      lawful permanent residents of the U.S. with a U.S. physical address or
      with military addresses (APO or FPO) who are at least 18 years old (or the
      age of majority in such person's home jurisdiction) and who have a valid
      social security number or tax identification number.
    </p>

    <h1>4. Use of Your Card</h1>

    <p>
      To use your Card, you must first log-in to the App and identify the Linked
      Accounts that you want linked to your Card Account to pay any amounts you
      may owe under this Agreement. You must maintain at least one Linked
      Account at all times and agree that we may directly debit any Linked
      Account for payment of amounts owed in accordance with the terms of this
      Agreement.
    </p>

    <p>
      You may use your Card for Purchases we may permit from time to time up to
      the amount of your Spending Limit. You agree we may honor and process all
      Purchases made in connection with your Card, even if such Purchases or
      transactions are not contemplated or intended by you. You agree that we
      may rely on information provided by merchants and the Card Network.
    </p>

    <p>
      You may use your Card to make Purchases or transactions for personal,
      family, or household purposes from any person or establishment that
      accepts the Card. We are not responsible for any losses you incur if we do
      not authorize any Purchase or if anyone refuses to accept your Card for
      any reason. You agree that you will not use your Card and/or Card Account
      for any business purpose, or for any unauthorized or illegal purpose. We
      reserve the right to close your Card and Card Account if we determine that
      is used for any impermissible purpose, as we may determine from time to
      time.
    </p>

    <p>
      You may not use your Card and Card Account for illegal gambling or any
      other illegal purchase or transaction, and you will be responsible for any
      such purchases or transactions if you do. You understand that this
      prohibition includes any purchase or transaction that is illegal in the
      jurisdiction where you live, where the transaction is made, or in any
      other jurisdiction affected by the transaction. You are responsible for
      determining the legality of your purchase or transaction in all applicable
      jurisdictions before making a purchase or entering a transaction. We have
      no obligation to review, monitor, evaluate or determine the legality of
      any purchase or transaction made using your Card or Card Account. We
      reserve the right, however, to decline, reject, refuse or deny any
      purchase or transaction that we believe may be illegal, related to an
      illegal activity, or a high-risk transaction. To the fullest extent
      permitted by law, you agree to pay for all Purchases and transactions made
      with your Card and Card Account, even if such Purchase or transaction is
      determined to be illegal, impermissible or associated with any illegal or
      unauthorized activity. We also reserve the right to cancel, close or
      restrict the use of the Card and Card Account, including to decline and
      refuse to authorize and process any transaction, if we believe you have
      violated this Agreement or applicable law.
    </p>

    <p>
      You understand that our services, including access to the Card and Card
      Account, may not always be available, and that we are not liable for any
      losses that may result when such services are unavailable due to any
      reason outside of our control, including, but not limited to, failure of
      any machine, merchant, financial institution or any other party to honor
      your Card.
    </p>

    <h1>5. Additional Card Use Terms</h1>

    <p>Single-Use and Multi-Use Virtual Cards</p>

    <p>
      Your Card Account allows you to generate virtual Cards. You may create:
    </p>

    <p>
      Single-use virtual Cards, which can be used for one transaction only. Once
      used, the card number associated with the virtual Card expires and cannot
      be reused.
    </p>

    <p>
      Multi-use virtual Cards, which may be used for multiple transactions (
      until the virtual Card expires or up to any usage limits you set are
      reached. These virtual Cards can be configured for recurring use (e.g.,
      e.g., daily, weekly, monthly) or for a fixed number of uses, depending on
      your preferences and the App's capabilities. These virtual Cards are
      accepted wherever the Card Network is supported and may offer enhanced
      security features, including customizable expiration dates, spending
      limits and merchant restrictions. We do not charge any fees for creating
      single or multi-use virtual Cards. All virtual card data is protected in
      accordance with applicable data security standards, including PCI DSS,
      where required.
    </p>

    <h1>6. Card Not Present Transactions.</h1>

    <p>
      If you use the Card number without presenting the Card in person (such as
      for mail order, telephone, or internet purchase), the legal effect will be
      the same as if you used the Card itself.
    </p>

    <h1>7. Promise to Pay.</h1>

    <p>
      You promise to pay us the amounts of each purchase and each transaction,
      together with all charges, fees and any other amounts charged to your Card
      and/or Card Account. This includes amounts where you did not sign a
      purchase slip or other document for the transaction. If you let someone
      else use your Card or Card Account, you are responsible for all charges
      made by that person, whether or not you have notified us that the person
      will be using your Card or Card Account and whether or not the amount of
      the actual use exceeds your permission. To the extent permitted by
      applicable law, you agree to pay all costs and disbursements, including
      reasonable attorney fees, incurred by us in legal proceedings to collect
      or enforce your indebtedness and/or the terms of this Agreement.
    </p>

    <h1>8. Authorizations</h1>

    <p>
      We do not guarantee approval of transactions or any purchase. We reserve
      the right to deny transactions or purchases for any reason, such as
      exceeding your Spending Limit, account default, suspected fraudulent or
      unlawful activity, internet gambling, or any indication of increased risk
      related to the transaction or purchase. We also may limit the number of
      authorizations we allow during a period of time. When you use your Card or
      Card Account to initiate a transaction or purchase at certain merchant
      locations, such as hotels, restaurants, gas stations, and rental car
      companies, where the final purchase amount is unknown at the time of
      authorization, a hold may be placed by the merchant on the available
      credit in your Card Account for an amount equal to or in excess of the
      final transaction or purchase amount. The credit amount subject to the
      hold will not be available to you for any other purpose until the merchant
      sends us the final transaction or purchase amount. Once we receive the
      final transaction or purchase amount, it may take several days for the
      hold to be removed. During that period, you will not have access to the
      credit amount subject to the hold. If you give your Card or Card Account
      information to a merchant to bill your Card or Card Account for recurring
      payments, or to keep it on file for future purchases or payments, and your
      Card number, expiration date, or security code changes, you should notify
      the merchant with your new Card and Card Account information.
    </p>

    <p>
      You are responsible for all repayment obligations based on how the
      purchase or transaction is categorized. Merchants generally are permitted
      to request authorization only for the actual amount of a transaction.
      However, certain kinds of merchants in specified situations are permitted
      to request authorization for a Card transaction in an amount different
      from the amount of the actual transaction. For example, restaurants and
      drinking establishments may request authorization for the estimated amount
      of a transaction, and others may request authorization for an amount up to
      20% over the amount of the actual bill in order to cover an anticipated
      tip. When we receive an authorization request from a merchant, we do not
      receive information from which to determine whether the request covers the
      actual amount of a transaction, an estimated amount, or whether the
      merchant's request exceeds the amount permitted and you agree that you
      remain obligated to us for the excess amount charged to your Card despite
      that it may exceed your Spending Limit.
    </p>

    <h1>9. Disputed Transactions</h1>

    <p>
      You agree to inspect each Statement you receive and contact us about any
      errors or questions you have, as described in "Billing Rights" below. If
      you do not notify us of an error, we will assume that all information on
      the Statement is correct. If we credit your Card Account for all or part
      of a disputed transaction or purchase, you give us all of your rights
      against others regarding that transaction or purchase, and will also: (1)
      give us any information about the disputed transaction or purchase, if we
      ask; (2) not pursue any claim or reimbursement of the transaction or
      purchase amount from the merchant or any other person; and (3) help us get
      reimbursement from others.
    </p>

    <h1>10. Spending Limits.</h1>

    <p>
      Your card does not have a preset spending limit; however, this does not
      mean unlimited spending. We may impose internal limits on your Card
      Account based on factors such as your payment history, account activity
      and other risk considerations. These limits are determined by us and may
      change at any time without prior notice. Transactions may be declined if
      they exceed these limits or if we otherwise determine that additional risk
      controls are necessary.
    </p>

    <p>
      You are required to pay the full balance shown on your billing statement
      by the due date each billing cycle, regardless of any internal limits we
      apply. The absence of a disclosed preset spending limit does not relieve
      you of your obligation to pay in full each cycle. We may also require
      additional information or verification before approving certain
      transactions that approach or exceed your internal limit.
    </p>

    <h1>11. Foreign Transactions and Foreign Conversion Fee</h1>

    <p>
      A foreign transaction is any transaction (1) made in a foreign currency,
      or (2) made in U.S. dollars if the transaction is made or processed
      outside of the United States. This includes online transactions made in
      the U.S. but processed by a foreign merchant or processor.
    </p>

    <p>
      If a transaction is made in a foreign currency, the Card Network will
      convert the charge into a U.S. dollars in accordance with its operating
      regulations and use its conversion procedures in effect at the time the
      transaction is processed. The conversion rate may differ from the rate on
      the transaction date or posting date. All foreign transaction fees
      assessed by any Card Network or any other third party will be posted to
      your Card Account. The foreign transaction fee set forth in the fees table
      under Section 13("Fees") is in addition to any conversion rate assessed by
      the Card Network or any third party.
    </p>

    <h1>12. Interest</h1>

    <p>
      We do not charge interest, including penalty interest, for the use of the
      Card.
    </p>

    <h1>13. Fees</h1>

    <p>
      We do not charge any fees in connection with your Card Account, including
      annual or participation fees, transaction fees, penalty fees (such as late
      payment or returned payment fees), or foreign transaction fees, as
      indicated in the Important Disclosures About Your Charge Card table. If
      any fees are introduced in the future, we will provide notice and amend
      this Agreement in accordance with the "Changes to This Agreement" section
      and applicable law.
    </p>

    <h1>14. Statements</h1>

    <p>
      We will generally send or make available to you a Statement for your Card
      and Card Account after the end of each Billing Cycle. The transaction
      history will remain available to you in your dashboard. However, under
      certain circumstances, the law may not require us to send or make a
      Statement available to you or may prohibit us from doing so. Each
      Statement covers a single Billing Cycle. We send only one Statement for
      all Cards with your Card Account number. If there are two or more of you,
      you agree that all Statements and notices regarding the Card and Card
      Account may be sent solely to the email address shown on our billing
      records. The Statement will show the transactions billed to your Card
      Account during the Billing Cycle, your previous Card Account balance, your
      New Balance, your Payment Due Date and additional information. Please
      review each Statement carefully. You must notify us promptly of any change
      in your name, residence or billing address by emailing us at or contacting
      us at {{ SUPPORT_EMAIL }}. Preservation of your rights under federal law
      regarding resolution of billing errors depends upon your timely
      recognition of potential errors, as explained below. We will not send your
      Statement if (a) we deem your Card Account uncollectible, (b) delinquency
      collection proceedings have been instituted, or (c) for any other reason
      permitted by applicable law. The "closing date" is the last day of the
      Billing Cycle. All transactions posted after the closing date will appear
      on your next Statement.
    </p>

    <h1>15. Payment</h1>

    <p>
      You agree to pay us in full each month for the entire New Balance and any
      additional amounts owed under this Agreement as provided in your
      Statement. Your payment is due on or before the Payment Due Date shown on
      your Statement, which will be at least 21 days after your Statement date.
      You can pay your Statement balance through the App by using either an
      electronic Preauthorized Transfer or an electronic One Time Transfer as
      described below. Each time you authorize us to initiate a payment from
      your Linked Account, you are promising us that you are legally authorized
      to use the Linked Account(s) to make the payment. When you provide us with
      your Linked Account information, you authorize us to (i) retain the
      information to initiate payments based on your instructions; (ii) credit
      your Linked Account; and (iii) correct any payment, credit, or other
      errors.
    </p>

    <p>Preauthorized Transfers.</p>

    <p>
      You may authorize us to initiate recurring or automatic payments from your
      Linked Account(s) for each Statement balance or other payments to your
      Card Account ("AutoPay"). If you select AutoPay, you authorize us to
      initiate repayment from your Linked Account(s) (A) for the amount of the
      purchase or transaction after you complete a purchase or transaction, and
      (B) for the Statement balance by the Payment Due Date. If more than one of
      your Linked Accounts is set up for AutoPay, and if your primary Linked
      Account lacks sufficient funds to cover the amount of your AutoPay, we
      will deduct the remaining amounts from your alternative Linked Account
      that are set up for AutoPay. It is important that you correctly set your
      default Linked Account in App for repayment purposes. If you have multiple
      Linked Accounts, we will initiate repayments starting with your default
      account and then from any additional Linked Accounts you have designated,
      in the order you select. We are not responsible for any overdraft fees or
      any penalties imposed on you by the issuer of your Linked Account that
      result from our attempt to process an AutoPay payment.
    </p>

    <p>One-Time Transfers:</p>

    <p>
      You may initiate a one-time transfer ("One-Time Transfer") from your
      Linked Account by logging into the App and selecting a one-time transfer.
    </p>

    <p>
      You may cancel AutoPay or any scheduled payment by contacting us. We must
      receive your cancellation request 3 business days before your scheduled
      payment.
    </p>

    <p>
      We may cancel or reject any AutoPay or One-Time Transfer you attempt to
      make that does not comply with the payment instructions set forth under
      this Agreement or on your Statement or for any reason we deem appropriate,
      and we will notify you of such cancellation or rejection. We may permit
      new payment methods in our discretion.
    </p>

    <p>
      If the Billing Cycle ends on a day that falls on a weekend or a U.S.
      federal holiday, we may debit your Linked Account via AutoPay on the last
      business day prior to the end of the Billing Cycle.
    </p>

    <p>
      Payments initiated by 11:59 p.m. (EST) will be credited that same day.
    </p>

    <p>
      You may pay all or any part of Card Account balance at any time without
      incurring any prepayment charge or penalty. Prepayments will reduce the
      amount you owe us, but you must still pay the full outstanding balance by
      the Payment Due Date shown on each Statement.
    </p>

    <p>
      We may elect in our discretion to reject or accept any payment if there is
      a credit balance on your Card Account or would be a credit balance after
      such payment. We will not pay interest on any credit balance.
    </p>

    <p>
      Generally, credits to your Card Account, such as those generated by
      merchants or by person-to-person money transfers, are not treated as
      payments and will not satisfy your obligation to pay your Statement
      balance by the Payment Due Date.
    </p>

    <h1>16. Payment Application and Instructions</h1>

    <p>
      Subject to applicable law, when we receive a payment for your Card
      Account, we apply amounts in any manner we choose. If you do not pay your
      New Balance, as shown on your Statement, in full each month, you may not
      be able to use your Card Account to initiate new Cards to make new
      purchases. We apply payments to balances as they appear on your Statement
      before being applied to new transactions. An example of a new transaction
      is a recent purchase you made that has not yet been included in the New
      Balance as shown on your Statement.
    </p>

    <p>
      All payments must be made in U.S. dollars drawn on funds on deposit in the
      United States. We will reject payments that are not drawn in U.S. dollars
      and funds drawn on a financial institution located outside of the United
      States. Payments on your Card Account should be made from your Linked
      Account via One-Time Transfers or by setting up AutoPay. You agree not to
      make any payment by cash, cash advance, or any method other than an
      acceptable Linked Account. If you attempt to make payment by any
      prohibited method, the payment will be rejected, and your Card Account may
      be subject to further review and potential restrictions. We do not
      generally accept checks as a method of payment for your Card Account,
      however, if we do accept a check, we may, in our sole discretion, accept
      late payments, partial payments, and payments marked "paid in full", or
      "without recourse", or any payment restrictively endorsed without waiving
      our right to payment in full of your entire Card Account balance or our
      other rights under this Agreement. Subject to applicable law, we may make
      third-party services available to you that allow you to make faster or
      recurring payments online or by telephone. We or the third party providing
      such services will describe the terms of such services before you use
      them, including any fees (which will be third-party fees and not fees of
      our own).
    </p>

    <p>
      We may delay, limit or restrict the use of your Card Account, including
      the availability of credit for use, until we confirm that any payment you
      have made has cleared and funds have been received by us. This may happen
      even if we initially or provisionally credit your payment to your Card
      Account.
    </p>

    <p>
      We may resubmit and collect returned payments electronically. If
      necessary, we may adjust your Card Account to correct errors, process
      returned and reversed payments and handle similar issues. We may reject
      and return any payment that creates or adds to a credit balance on your
      Account. We may without notice restrict the availability of any credit
      balance in our sole and absolute discretion. We may reduce the amount of
      any credit balance by any new charges or if we have a reasonable belief
      that any balance may not be collectible from you, subject to applicable
      law. We may also adjust your Spending Limit if we have a reasonable belief
      that the balance on your Card Account may not be collectible from you,
      subject to applicable law. You may send an email to {{ SUPPORT_EMAIL }}or
      call the number on the back of your Card to request a refund of any
      available credit balance over $1, which we will process within 7 business
      days of receiving your request. If you do not request a refund, we will
      apply the credit balance to new purchases and transactions unless we are
      required by law to provide you with a refund.
    </p>

    <h1>17. Authorization to Link a Linked Account to Your Card Account</h1>

    <p>
      To add a Linked Account to your Card Account, the third-party service that
      we use may ask you to provide your online banking credentials. We
      currently use Plaid for this service, but we may use others. You can only
      provide credentials for Linked Accounts that you own. By providing your
      credentials to the third-party service provider, you authorize us to use
      the information from your Linked Account to provide you with services for
      your Card Account, including to help determine your Spending Limit. Your
      authorization will remain in effect until you revoke it, which may affect
      your ability to receive the services. Plaid's terms of use and privacy
      policy are here:
      <a
        href="https://plaid.com/legal"
        target="_blank"
        rel="noopener noreferrer"
      >
        https://plaid.com/legal
      </a>
      .
    </p>

    <h1>18. Authorization to Debit Linked Account</h1>

    <p>
      You authorize us to collect amounts owed under this Agreement by debiting
      your Linked Account as provided in Section 15 of this Agreement. You
      acknowledge and agree that this authorization will remain in full force
      and effect until you notify us in writing that you wish to revoke this
      authorization. If you withdraw the debit authorization from all Linked
      Accounts, you must pay all transactions, fees, and other amounts not yet
      reflected on your Statement immediately. You may do so via manual payment
      methods provided in the App.
    </p>

    <p>
      Any debit we initiate via ACH will be governed by the National Automated
      Clearinghouse Association's rules for consumer-related ACH debits.
    </p>

    <p>
      If an automatic debit fails, we may attempt to debit the Linked Account
      again. If there is an error in processing, you authorize us to correct the
      error by initiating an electronic credit or debit to the relevant Linked
      Account and we will notify you of any correction made.
    </p>

    <h1>19. Authorized Users</h1>

    <p>
      If you ask us to issue a Card to an Authorized User, and we approve your
      request, we may require certain information about them, and you should let
      them know that you are sharing their information with us. We may limit
      their ability to use your Card Account. They may have access to certain
      information about your Card Account. You will be liable, to the fullest
      extent permitted by law, for their use of the Card Account and anyone else
      they allow to use your Card Account, even if you did not want, or agree
      to, that use. For purposes of your liability for transactions under the
      Card Account and except as otherwise provided by law, the term Authorized
      User also includes any person to whom you permit to use or provide access
      to a Card, a device that can access the Card Account or authentication
      credentials for the Account.
    </p>

    <p>
      If you want to remove an Authorized User from your Account, you must
      contact Program Manager via the App or by emailing {{ SUPPORT_EMAIL }}and
      request their removal. You also must immediately ensure any Cards or
      virtual credentials are returned or destroyed and disabled. They will be
      able to use your Card Account until you have notified us that you are
      removing them from your Card Account. During this time, you will still be
      responsible for all amounts they charge to your Card Account to the
      fullest extent permitted by law. You will be responsible even if these
      amounts do not appear on your Card Account until later. In some cases,
      when an Authorized User is removed, we may need to close your Card
      Account, open a new account and issue a new Card to you.
    </p>

    <h1>20. Recurring Transactions</h1>

    <p>
      If you authorize a merchant or any other person to charge your Account for
      recurring transactions, you must ensure that you are eligible to use your
      Spending Limit when the merchant or other person charges your Card
      Account. If your Card Account has a balance, your Card Account is closed
      or suspended, your Card number changes, or there is any other reason for
      which you are not eligible to use your Spending Limit and we decline the
      transaction, we will not be responsible for any losses associated with the
      declined recurring transaction. If your Card Account is closed, suspended,
      or your Card number changes, you are responsible for contacting the
      merchant or other person you are paying by a recurring transaction, and
      for any amounts owed to us.
    </p>

    <h1>21. Cash Advances and Balance Transfers.</h1>

    <p>
      Cash advances and balance transfers are not permitted on your Card
      Account. Any attempt to initiate such a transaction may be declined. We
      are not liable for any fees or losses incurred as a result of declined
      transactions of this nature.
    </p>

    <h1>22. Credit Reports</h1>

    <p>
      We do not obtain credit reports or report your Card Account activity to
      credit bureaus.
    </p>

    <h1>23. Third Party Rewards Programs</h1>

    <p>
      In connection with the Card, you may be provided with the opportunity to
      earn certain rewards, participate in subscription programs, promotions or
      other benefits or services provided and separately offered to you by one
      or more other parties (including, our Program Manager) (each, a "Rewards
      Program"). Any Rewards Program is governed by separate terms provided by
      the applicable provider. You acknowledge and agree that any such Rewards
      Program is not a part of this Agreement, and we are not responsible for
      providing the management and services for any Rewards Program, including
      points, promotional offerings, membership subscriptions, redemptions for
      merchandise or other benefits or services and that we do not manage,
      service or otherwise participate in such Rewards Program with any other
      party (including our Program Manager). We will not have any obligation or
      liability to you for your participation in any Rewards Program or the
      management or servicing by any other party (including by Program Manager)
      of any Rewards Program. You further understand and acknowledge that any
      Rewards Program will be governed by separate terms and conditions provided
      to you by the applicable entity associated with the Rewards Program. If
      you have any questions regarding the Rewards Program, please contact the
      entity providing the Rewards Program.
    </p>

    <h1>24. Account Default and Cost of Collection</h1>

    <p>Account Default.</p>

    <p>
      If you are in default, we can charge you fees as set forth herein, lower
      or restrict your Spending Limit, file a lawsuit against you and/or pursue
      another action not prohibited by law, declare the entire balance of your
      Card Account due and payable at once without notice or demand, suspend,
      restrict or cancel your Card and Card Account and/or terminate this
      Agreement without liability. You are in default if: (i) you fail to make a
      required payment when due or by the Payment Due Date; (ii) you remove all
      your Linked Accounts from your Card Account and do not provide an
      alternative payment method to us; (iii) we determine you made a false or
      misleading statement to us at any point in relation to your application
      for a Card Account or this Agreement, or otherwise attempted to defraud
      us; (iv) you are subject to a bankruptcy or insolvency proceeding; (v) you
      die, are declared incompetent, or become insolvent; (vi) a receiver is
      appointed or a writ or order of attachment, levy or garnishment is issued
      against you or any of your property assets or income; (vii) any payment
      you make is rejected, not paid or cannot be processed; (viii) you exceed
      your Spending Limit; (ix) you permanently reside outside of the U.S.; (x)
      you fail to comply with any term of this Agreement; or (xi) we believe in
      good faith, that the likelihood of you paying or performing all your
      obligations under this Agreement is impaired. If your Account is past due
      90 days or more, is part of a bankruptcy proceeding or is otherwise
      charged off, the total balance is immediately due and payable. In the
      event of your default, and subject to any limitations or requirements of
      applicable law, you agree to pay all costs, including costs of
      collections, reasonable attorney's fees and court costs (including costs
      incurred in bankruptcy and appellate court proceedings) that are incurred
      by us in collecting all amounts due on your Card Account, whether or not
      suit is brought against you, and in protecting ourselves from any harm
      that we may suffer as a result of your default.
    </p>

    <p>Cost of Collections.</p>

    <p>
      Except as noted below, in the event of default you agree to pay all costs
      incurred by us in collecting any amount you owe or in enforcing or
      protecting our rights under this Agreement as permitted by applicable law.
      Subject to applicable law, costs of collection include, but are not
      limited to, collection agency fees, repossession fees, court fees or fees
      for any other judicial proceeding, and reasonable attorneys' fees for any
      action taken by an attorney, which may include a salaried employee if
      state law allows.
    </p>

    <h1>25. Suspension and Termination</h1>

    <p>
      We may, at any time and for any reason, suspend or close your Card and
      Card Account or otherwise terminate your right to use your Card and Card
      Account, even if you are not in default. You may close your Card and Card
      Account at any time by sending us an email to {{ SUPPORT_EMAIL }}. Your
      obligations under this Agreement continue even after the Card and Card
      Account is closed. You must destroy all Cards or other credit devices on
      the Card Account when the Card Account is closed. When your Card Account
      is closed, you must contact anyone authorized to charge transactions to
      your Card Account, such as any subscription services. These transactions
      may continue to be charged to your Card Account until you change your
      billing information with such third parties. If we believe you have
      authorized a transaction or are attempting to use your Card and/or Card
      Account after you have requested to close the Card and Card Account, we
      may allow the transaction to be charged to your Card Account.
    </p>

    <h1>26. Security; Lost or Stolen Card; and Unauthorized Use</h1>

    <p>
      You must take reasonable steps to prevent the unauthorized use of your
      Card, Card Account and/or Card Account number. We reserve the right to
      request you take specific steps to limit access to or prevent unauthorized
      use of your Card and/or Card Account. For security reasons, you must
      comply with any card activation procedures that may be prescribed by us.
      If a Card is lost or stolen or if you believe someone else may be using
      your Card or Card Account without your permission, you must notify us
      immediately by sending us an email at {{ SUPPORT_EMAIL }} and take
      reasonable actions we may request to prevent unauthorized use of your Card
      and/or Card Account.
    </p>

    <p>
      Reporting a lost/stolen Card or unauthorized transactions through [+1
      (888) 857-5490], logging into the App or sending an email to
      {{ SUPPORT_EMAIL }}
      to deactivate the Card is the best way to minimize possible losses.
    </p>

    <h1>27. No Warranty Regarding Merchant Goods or Services</h1>

    <p>
      We are not responsible for the quality, safety, legality, or any other
      aspect of any goods or services you purchase with your Card and Card
      Account beyond your rights described in the Billing Rights below.
    </p>

    <h1>28. Assignment and Sharing Information</h1>

    <p>
      We may at any time, and without notice to you, sell, assign or transfer
      your Account, any amounts due on your Account, this Agreement, or rights
      or obligations under your Account or this Agreement to any person or
      entity, including our Program Manager. The person or entity to whom we
      make any such sale, assignment or transfer shall be entitled to all of our
      rights and shall assume our obligations under this Agreement, to the
      extent sold, assigned or transferred, and shall have the right to, without
      notice to you, sell, assign or transfer your Account, any amounts due on
      your Account, this Agreement, or rights or obligations under your Account
      or this Agreement to any other person or entity For information regarding
      our privacy practices for your account, please refer to our Privacy Policy
      located at
      <a
        href="https://www.cfsb.com/wp-content/uploads/2023/03/CFSB-Privacy-Notice_December-2023-FINAL.pdf"
        target="_blank"
        rel="noopener noreferrer"
      >
        https://www.cfsb.com/wp-content/uploads/2023/03/CFSB-Privacy-Notice_December-2023-FINAL.pdf
      </a>
      .
    </p>

    <h1>29. Change in Terms</h1>

    <p>
      We reserve the right to amend this Agreement at any time, by adding,
      deleting, or changing provisions of this Agreement. We will provide notice
      only to the extent required by applicable federal law or the laws of New
      York that are in effect at that time. If an amendment gives you the
      opportunity to reject the change, and if you reject the change in the
      manner provided in such amendment, we may terminate your right to receive
      credit and may ask you to return all credit devices as a condition of your
      rejection. If you use your Card after the effective date of any change,
      you will be considered to have agreed to the new terms even if you have
      sent us such written notice. We may replace your Cards with other Cards at
      any time.
    </p>

    <h1>30. Program Manager</h1>

    <p>
      The Program Manager services parts of your Card and Card Account,
      including, but not limited to, Card Account and/or Card management through
      the App and customer service relating to any rewards, subscriptions
      services offered in connection with your Card or Card Account. In this
      capacity, Program Manager may act on our behalf, perform our obligations
      or enforce our rights under this Agreement. You understand and acknowledge
      that we may share with Program Manager any information you provide to us
      in connection with your Card and Card Account or any information we
      collect in connection with your Card and Card Account in order for Program
      Manager to provide you with products and/or services in connection with
      your Card and/or Card Account.
    </p>

    <h1>31. Military Lending Act</h1>

    <p>
      Federal law provides important protections to members of the Armed Forces
      and their dependents relating to extensions of consumer credit. In
      general, the cost of consumer credit to a member of the Armed Forces and
      his or her dependent may not exceed an annual percentage rate of 36
      percent. This rate must include, as applicable to the credit transaction
      or account: the costs associated with credit insurance premiums; fees for
      ancillary products sold in connection with the credit transaction; any
      application fee charged (other than credit application fees for specified
      credit transactions or accounts); and any participation fee charged (other
      than certain participation fees for a credit card account). To receive
      this information and a description of your payment obligation, please call
      +1 (888) 857-5490 or send an email to {{ SUPPORT_EMAIL }}.
    </p>

    <p>
      If any provision of this Agreement is contrary to the rights and
      protections afforded to you by the Military Lending Act and its
      implementing regulations, including, but not limited to 32 C.F.R. § 232.8,
      then the conflicting provisions or proscribed terms are inoperative, and
      shall have no force and effect. However, all remaining terms and
      provisions of this Agreement not proscribed or prohibited shall remain in
      full force and effect.
    </p>

    <h1>32. Governing Law</h1>

    <p>
      This Agreement is governed by and construed in accordance with the laws of
      the United States. To the extent state law applies to this Agreement, this
      Agreement will be governed by the laws of the State of New York. This
      Agreement is not valid until accepted by us in New York, and any credit
      extended to you is extended in and from New York, regardless of where you
      reside or use your Card or Card Account. You agree that if a dispute
      arises and you file suit against us, services of process on the us may be
      made only at our main office in New York.
    </p>

    <h1>33. Waiver; Entire Agreement; and Severability</h1>

    <p>
      We will not lose any of our rights if we delay or choose not to take any
      action for any reason. We may waive any rights without notifying you.
      Without limiting the generality of this section, we may waive fees or
      other charges that we may charge you without notifying you and without
      losing our right to charge them in the future. This Agreement is the final
      expression of the agreement between you and us and may not be contradicted
      by evidence of any alleged oral agreement. If any of the provisions of
      this Agreement (other than the requirement for arbitration in the
      Arbitration Clause) are held to be unenforceable or invalid for any
      reason, the remaining provisions hereof shall nevertheless remain
      enforceable and shall be interpreted in such a manner to preserve the
      enforceability of this Agreement to the maximum extent permitted by
      applicable law.
    </p>

    <h1>34. Notices</h1>

    <p>
      You agree to receive notices from us electronically via email to the
      address you provided, or by mail to your billing address. We may also send
      service-related alerts via text message to your mobile number. Notices are
      considered delivered when sent, unless we receive notification that the
      message was undeliverable. If a notice is undeliverable, we may attempt to
      resend it or deliver it by mail. By agreeing to electronic delivery, you
      confirm that you can access and retain electronic communications. You may
      withdraw consent at any time by contacting us, which may affect your
      ability to use certain services.
    </p>

    <h1>35. State Notices</h1>

    <p>
      The following notices apply only to residents of the states listed below.
      If you reside in one of these states, the notice for your state applies to
      your Card Account.
    </p>

    <p>
      These state-specific notices are provided to comply with applicable law
      and inform you of your rights. They do not constitute legal advice and
      should not be relied upon as such. You are encouraged to consult your own
      legal counsel regarding your rights and obligations under applicable state
      laws.
    </p>

    <p>
      This disclaimer is intended solely for informational and compliance
      purposes and is intended to waive, limit or alter any rights or remedies
      and is in no way intended to circumvent or diminish your substantive legal
      protections you may have under federal or state law.
    </p>

    <p>CALIFORNIA RESIDENTS:</p>

    <p>
      The applicant, if married, may apply for a separate account. After credit
      approval, as applicable: (a) each applicant shall have the right to use
      this account to the extent of any credit limit set by the creditor; and
      (b) each applicant may be liable for all amounts of credit extended under
      this account to each joint applicant.
    </p>

    <p>
      You are hereby notified that, in our discretion, a negative credit report
      reflecting on your credit record may be submitted to a credit reporting
      agency if you fail to fulfil the terms of your credit obligations.
    </p>

    <p>MASSACHUSETTS RESIDENTS:</p>

    <p>
      Massachusetts law prohibits discrimination based on marital status or
      sexual orientation.
    </p>

    <p>MISSOURI AND NEBRASKA RESIDENTS:</p>

    <p>
      Oral agreements or commitments to loan money, extend credit or to forbear
      from enforcing repayment of a debt, including promises to extend or renew
      such debt, are not enforceable. To protect you (borrower) and us
      (creditor) from misunderstanding or disappointment, any agreements we
      reach covering such matters are contained in this Agreement, which is the
      complete and exclusive statement of the agreement between you and us,
      except as we may later agree in writing to modify it.
    </p>

    <p>NEW JERSEY RESIDENTS:</p>

    <p>
      The section headings of the Agreement are a table of contents and not
      contract terms. Portions of this Agreement with references to actions
      taken to the extent of applicable law apply to acts or practices that New
      Jersey law permits or requires. In this Agreement, acts or practices (i)
      by you which are or may be permitted by 'applicable law' are permitted by
      New Jersey law, and (ii) that may or will be taken by you unless
      prohibited by 'applicable law' are permitted by New Jersey law. Because
      certain provisions of this Agreement are subject to applicable laws, they
      may be void, unenforceable or inapplicable in some jurisdictions. None of
      these provisions, however, are void, unenforceable or inapplicable in New
      Jersey.
    </p>

    <p>NEW YORK RESIDENTS:</p>

    <p>
      New York residents may contact the New York State Department of Financial
      Services at (800) 342-3736 or
      <a
        href="https://www.dfs.ny.gov/"
        target="_blank"
        rel="noopener noreferrer"
      >
        https://www.dfs.ny.gov/
      </a>
      for free information on a comparative credit card rates, fees and grace
      periods.
    </p>

    <p>OHIO RESIDENTS:</p>

    <p>
      The Ohio laws against discrimination require that all creditors make
      credit equally available to all creditworthy customers and that credit
      reporting agencies maintain separate credit histories on each individual
      upon request. The Ohio Civil Rights Commission administers compliance with
      this law.
    </p>

    <p>SOUTH DAKOTA RESIDENTS:</p>

    <p>
      If you believe there have been any improprieties in making this loan or in
      the lender's loan practices, you may contact the South Dakota Division of
      Banking at 1714 Lincoln Ave., Suite #2, Pierre, SD 57501 or by phone at
      (605) 773-3421.
    </p>

    <p>WASHINGTON RESIDENTS:</p>

    <p>
      Oral agreements or commitments to loan money, extend credit or to forbear
      from enforcing repayment of a debt, including promises to extend or renew
      such debt, are not enforceable. To protect you (borrower) and us
      (creditor) from misunderstanding or disappointment, any agreements we
      reach covering such matters are contained in this Agreement, which is the
      complete and exclusive statement of the agreement between you and us,
      except as we may later agree in writing to modify it.
    </p>

    <p>WISCONSIN RESIDENTS:</p>

    <p>
      If the credit card for which you are applying is granted, you will notify
      Program Manager if you have a spouse who needs to receive notification
      that credit has been extended to you.
    </p>

    <p>
      Your signature confirms that this loan obligation is being incurred in the
      interest of your marriage or family. No provision of any marital property
      agreement, unilateral statement or court decree adversely affects a
      creditor's interest unless, prior to the time the credit is granted, the
      creditor is furnished a copy of the agreement, statement or decree or has
      actual knowledge of the adverse provision.
    </p>

    <p>THE FOLLOWING IS REQUIRED BY VERMONT LAW:</p>

    <p>
      NOTICE TO CO-SIGNER: YOUR SIGNATURE ON THIS NOTE MEANS THAT YOU ARE
      EQUALLY LIABLE FOR REPAYMENT OF THIS LOAN. IF THE BORROWER DOES NOT PAY,
      THE LENDER HAS THE LEGAL RIGHT TO COLLECT FROM YOU.
    </p>

    <p>NOTICE TO ALL VERMONT, NEW YORK AND RHODE ISLAND RESIDENTS:</p>

    <p>
      We may obtain at any time your consumer reports, for any legitimate
      purpose associated with the Account or the application or request for an
      Account, including but not limited to reviewing, modifying, renewing and
      collecting on your Account. On your request, you will be informed if such
      a report was ordered. If so, you will be given the name and address of the
      consumer reporting agency furnishing the report.
    </p>

    <h1>36. Survival.</h1>

    <p>
      Notwithstanding anything herein to the contrary, the provisions of this
      Agreement will survive termination to the extent necessary to effectuate
      their respective purposes.
    </p>

    <h1>37. Digital Wallet Terms.</h1>

    <p>
      This subsection governs your access to, and use of your Card through a
      digital wallet provided by another company (such as Apple Pay, Google Pay,
      Samsung Pay, Amazon, PayPal, Venmo, etc.) or a merchant-branded website or
      digital wallet where your Card information is stored for future use (each,
      a "Digital Wallet").
    </p>

    <p>Adding your Card to a Digital Wallet:</p>

    <p>
      To add your Card to a Digital Wallet, you must link your Card with that
      Digital Wallet by following the instructions of the provider of the
      Digital Wallet or another third party supporting the Digital Wallet
      (collectively, the "Digital Wallet Provider"). You may be required to take
      additional steps to authenticate yourself or the Card before your Card is
      added to the Digital Wallet. We may not add a Card to your Digital Wallet
      if we cannot authenticate the Card or if we otherwise suspect that there
      may be fraud associated with the Card.
    </p>

    <p>
      You may only use a Digital Wallet with a compatible device, as determined
      by the Digital Wallet Provider ("Compatible Device").
    </p>

    <p>
      If you request to add your Card to a Digital Wallet, you authorize us to
      collect, transmit, store, use and share information with third parties
      (including, but not limited to, the Digital Wallet Provider and applicable
      payment card networks) about you, your Card, your Compatible Device, and
      your use of the Card to the extent necessary to effectuate the addition of
      your Card, and the use of your Card, in the applicable Digital Wallet(s)
      to which you have requested to add your Card.
    </p>

    <p>
      We reserve the right to add or remove the Digital Wallets in which you may
      add or use your Card in our sole discretion.
    </p>

    <p>Using Your Card in a Digital Wallet:</p>

    <p>
      Once you add a Card to a Digital Wallet on a Compatible Device, you may
      use the Compatible Device to make payments utilizing the Card at any
      merchant that accepts the Digital Wallet and Card, subject to your
      agreement with the Digital Wallet Provider. By selecting an added Card in
      the Digital Wallet and using merchant's contactless-enabled point-of-sale
      terminal or reader or by using a Card for an in-app or a website purchase,
      you are authorizing the payment for the applicable merchant's products or
      services with that Card in the Digital Wallet. The Digital Wallet may
      display transaction history, but it does not reflect any
      post-authorization activities and may not match the actual transaction
      details that are posted to your Card Account.
    </p>

    <p>
      A Digital Wallet may not be accepted at all merchants where your Card is
      otherwise accepted, and your Card may not be eligible for all the features
      and functionalities offered by the Digital Wallet.
    </p>

    <p>
      Any virtual Card we may issue you that is linked with the Digital Wallet
      may be, in our sole discretion, automatically updated or upgraded without
      notice to you.
    </p>

    <p>
      We reserve the right to restrict the use of Cards within a Digital Wallet
      at any time and for any reason. We may terminate your access to, or use of
      your Card, with a Digital Wallet at any time and for any reason, including
      if you violate any of the terms or conditions of these Digital Wallet
      Terms. Some examples of instances where we may take action to restrict or
      terminate use of your Card in a digital wallet include: if we suspect
      fraud with your Card, if applicable law changes or if we are directed to
      do so by the Digital Wallet Provider or the relevant payment card network.
    </p>

    <p>
      You may remove your Card from a Digital Wallet by following the Digital
      Wallet Provider's procedures for removal. You are liable for purchases and
      transactions made through the Digital Wallet.
    </p>

    <p>Digital Wallet Provider Agreements:</p>

    <p>
      You understand and agree that your use of a Digital Wallet is subject to
      the terms and conditions set forth by the Digital Wallet Provider with
      respect to the use of that Digital Wallet. You acknowledge that we are not
      party to any agreement or the terms and conditions for a Digital Wallet
      between you and a Digital Wallet Provider, and we do not own and are not
      responsible for a Digital Wallet.
    </p>

    <p>
      Your agreement with any Digital Wallet Provider does not impact this
      Agreement. Any use of your Card in or through the Digital Wallet continues
      to be subject to all terms and conditions of this Agreement.
    </p>

    <p>Applicable Fees:</p>

    <p>
      We do not charge you any additional fees for adding your Card to a Digital
      Wallet or for using your Card in the Digital Wallet. However, any fees and
      charges that would apply when you use your Card outside the Digital Wallet
      will also apply when you use a Digital Wallet to make purchases with your
      Card or otherwise access your Card.
    </p>

    <p>
      The Digital Wallet Provider and other third parties (such as wireless
      companies or data service providers) may charge you fees, and you agree to
      be responsible for such fees.
    </p>

    <p>Limitation of Our Liability:</p>

    <p>
      We are not the provider of the Digital Wallet, and we are not responsible
      for providing the Digital Wallet service to you or for ensuring that your
      Card is compatible with any Digital Wallet service. We are not affiliated
      with any Digital Waller Provider, and we do not endorse any Digital Wallet
      Provider. We do not make any representation or warranty of any kind
      regarding the performance or operation of your Compatible Device or the
      Digital Wallet.
    </p>

    <p>
      Some Digital Wallets may use your palm print, fingerprint, facial map or
      any other biometric data to recognize you, authenticate your identity, or
      authorize your transactions, and you understand we do not provide any such
      technology or services and have no obligation regarding the security of
      such technology or services. By using such technology or services with a
      Digital Wallet to conduct any transaction, you are authorizing a
      transaction on your Card. We are only responsible for supplying
      information to the Digital Wallet provider to allow usage of your Card in
      the Digital Wallet as you have requested.
    </p>

    <p>
      In no event are we responsible for: (i) any failure of the Digital Wallet,
      any mobile device you use with the Digital Wallet, or the inability to use
      the Digital Wallet for any transaction or (ii) how the Digital Wallet
      Provider performs its services or any other third parties regarding any
      agreement you enter into with the Digital Wallet Provider or other third
      party. We do not control the privacy and security of any of your
      information that may be held by the Digital Wallet Provider. Any
      information held by the Digital Wallet Provider is governed by the Digital
      Wallet Provider's privacy policy
    </p>

    <p>
      TO THE MAXIMUM EXTENT PERMITTED BY LAW, UNDER NO CIRCUMSTANCES AND UNDER
      NO LEGAL THEORY ARE WE LIABLE FOR ANY LOSSES, CLAIMS, EXPENSES OR DAMAGES
      RESULTING FROM YOUR USE OF A DIGITAL WALLET, YOUR USE OF THE CARD OR
      VIRTUAL CARD IN CONNECTION WITH A DIGITAL WALLET, OR A MOBILE DEVICE. YOU
      EXPRESSLY ACKNOWLEDGE AND AGREE THAT THE USE OF A CARD PROVISIONED TO THE
      DIGITAL WALLET OF YOUR CHOICE AND THE DIGITAL WALLET SERVICE IS AT YOUR
      SOLE RISK.
    </p>

    <p>Security.</p>

    <p>
      If you use a Digital Wallet, you should protect your Digital Wallet and
      your Compatible Device as you would your Card. If your Digital Wallet or
      Compatible Device is compromised, lost or stolen, you should also consider
      your Card lost or stolen and notify us immediately. If your physical
      plastic Card is lost or stolen and your Compatible Device is not lost or
      stolen, you may be required to add the replacement physical plastic Card
      to a Digital Wallet. If your Compatible Device is lost or stolen, you will
      need to add your Card to a Digital Wallet on a new Compatible Device.
    </p>

    <p>Digital Wallet Provider Disclosures:</p>

    <p>Apple Pay:</p>

    <p>
      For additional information and terms regarding the use of Apple Pay, click
      here. We do not control or endorse the provisioning or use of Apple Pay.
    </p>

    <p>Google Pay:</p>

    <p>
      These Terms do not apply to transactions in Google products that are not
      initiated or effectuated through the Digital Wallet. Your use of Google
      Pay is also subject to the terms and conditions set forth in the Google
      Pay Terms of Service. We do not control or endorse the provisioning or use
      of Google Pay.
    </p>

    <p>Samsung Pay:</p>

    <p>
      Your use of Samsung Pay is also subject to the terms and conditions set
      forth in the Samsung Pay Terms and Conditions. We do not control or
      endorse the provisioning or use of Samsung Pay Money Transfer services.
    </p>

    <p>
      If you have any questions, disputes, or complaints about a Digital Wallet,
      contact the Digital Wallet Provider using the information given to you by
      the Digital Wallet Provider.
    </p>

    <h1>EXHIBIT A</h1>

    <h1>ARBITRATION CLAUSE</h1>

    <p>
      READ THIS ARBITRATION CLAUSE CAREFULLY AS IT INCLUDES A CLASS ACTION
      WAIVER AND WAIVER OF A TRIAL BY JURY. PLEASE KEEP A COPY OF THIS
      ARBITRATION CLAUSE FOR YOUR RECORDS.
    </p>

    <p>
      IF YOU DO NOT AGREE TO THE TERMS OF THIS ARBITRATION CLAUSE, DO NOT APPLY
      FOR AND/OR OPEN AN ACCOUNT.
    </p>

    <p>
      IF YOU ARE A COVERED BORROWER UNDER THE MILITARY LENDING ACT WHEN YOU OPEN
      YOUR ACCOUNT, THIS EXHIBIT A AND ARBITRATION CLAUSE DO NOT APPLY.
    </p>

    <h1>1. Informal Mediation.</h1>

    <p>
      Before formally pursuing a dispute in arbitration or small claims court,
      you agree to first send a detailed notice ("Notice") to Community Federal
      Savings Bank, Attn: Legal Department, 89-16 Jamaica Avenue, Woodhaven, NY
      11421 in case of a dispute with the Bank, and to Program Manager, Cloaked
      Pay, LLC, Attn: Legal Department, 116 E. 16th St., Unit 6, New York, NY
      10003. in case of a dispute with Program Manager. If we (or Program
      Manager) have a dispute with you, we, and our Program Manager agree to
      first send a Notice to you at your most recent email address on file with
      us, or if no email address is on file, other contact information
      associated with your Card Account. Your Notice must contain all of the
      following information: (1) your full name; (2) information that enables us
      to identify your Card Account, your address, mobile phone number, email
      address, and date of birth you used to register your Card Account if any;
      and (3) a detailed description of your dispute including the nature and
      factual basis of your claim(s) and the relief you are seeking with a
      corresponding calculation of your alleged damages (if any).
    </p>

    <p>
      You must personally sign this Notice for it to be effective. Our (or
      Program Manager's) Notice must likewise set forth a detailed description
      of its dispute, which shall include the nature and factual basis of its
      claim(s) and the relief it is seeking, with a corresponding calculation of
      our damages (if any). You and we (and/or the Program Manager) agree to
      then negotiate in a good faith effort to resolve the dispute. As part of
      these good faith negotiations, if we (or Program Manager) request a
      telephone conference with you to discuss your dispute, you agree to
      personally participate, with your attorney if you are represented by
      counsel. Likewise, if you request a telephone conference to discuss our or
      Program Manager's dispute with you, then we (or Program Manager) agree to
      have one representative participate. This informal process should lead to
      a resolution of the dispute. However, if the dispute is not resolved
      within 60 days after receipt of a fully completed Notice and the Parties
      have not otherwise mutually agreed to an extension of this informal
      dispute resolution time-period, you, we, or Program Manager may initiate
      an arbitration (subject to a Party's right to elect small claims court as
      provided below).
    </p>

    <p>
      Completion of this informal dispute resolution is a condition precedent to
      filing any demand for arbitration or small claims court action. Failure to
      do so is a breach of this Agreement. The statute of limitations and any
      filing fee deadlines will be tolled while you, we, or Program Manager
      engage in this informal dispute resolution process. Unless prohibited by
      applicable law, the arbitration provider shall not accept or administer
      any demand for arbitration and shall administratively close any
      arbitration unless the Party bringing such demand for arbitration can
      certify in writing that the terms and conditions of this informal dispute
      resolution process were fully satisfied. A court of competent jurisdiction
      shall have authority to enforce this provision and to enjoin any
      arbitration proceeding or small claims court action.
    </p>

    <h1>2. Scope of Arbitration.</h1>

    <p>
      All disputes, claims, or controversies between you and us, or between you
      and Program Manager arising out of or related to this Agreement or any
      aspect of the relationship between you and us (or Program Manager),
      whether based in contract, tort, statute, fraud, misrepresentation,
      consumer rights, constitution, regulation, ordinance or any other legal
      theory, will be resolved through final and binding arbitration before a
      neutral arbitrator instead of in a court by a judge or jury. Claims
      subject to arbitration include initial claims, counterclaims,
      cross-claims, and third-party claims. Such claims also include data breach
      or privacy claims arising from or relating directly or indirectly to our
      disclosure (or disclosure by a third party acting on our behalf) of any
      non-public personal information about you, and disputes arising from
      communications involving telephones, automatic dialing systems, artificial
      or prerecorded voice messages, SMS text messages or facsimile machines.
      Claims subject to arbitration also include disputes arising from facts,
      actions or omissions that occurred prior to the date of this Agreement.
      You agree that we, Program Manager and you are each waiving the right to
      trial by a jury. The only exceptions to mandatory arbitration are the
      following: (1) invocation of small claims under the procedures set forth
      under this Arbitration Clause; or (2) any claim for injunctive other
      equitable relief in a court of competent jurisdiction to prevent the
      actual or threatened infringement, misappropriation, or violation of
      either party's copyrights, trademarks, trade secrets, patents, or other
      intellectual property rights.
    </p>

    <p>
      Solely for purposes of this Arbitration Clause, the terms "we," us" and
      "our" include (a) us, our Program Manager, and our and our Program
      Manager's respective subsidiaries, affiliates, agents, employers,
      successors, and assigns and all of their employees, officers, directors
      and controlling persons, and (b) any other person or company who provides
      any goods or services in connection with the Agreement if you assert a
      claim against such other person or company in connection with a claim you
      assert against us. You, us and our Program Manager are also referred to
      under this Arbitration Clause individually as a "Party" or, collectively,
      as the "Parties".
    </p>

    <h1>3. Arbitration Rules.</h1>

    <p>
      The arbitration will be administered by National Arbitration and Mediation
      ("NAM"). NAM shall not accept or administer any demand for arbitration and
      shall administratively close any arbitration unless the Party bringing
      such demand for arbitration can certify in writing that the terms and
      conditions of the informal mediation process provided above is fully
      satisfied. Any arbitration between the Parties shall be administered by
      NAM in accordance with NAM's operative Comprehensive Dispute Resolution
      Rules and Procedures (the "NAM Rules") in effect at the time any demand
      for arbitration is filed with NAM, as modified by this Agreement. For a
      copy of the NAM Rules, please visit
      <a
        href="https://www.namadr.com/resources/rules-fees-forms"
        target="_blank"
        rel="noopener noreferrer"
      >
        https://www.namadr.com/resources/rules-fees-forms
      </a>
      or contact NAM at NAM's National Processing Center at 990 Stewart Avenue,
      1st Floor, Garden City, NY 11530 and email address commercial@namadr.com,
      or call NAM at (800) 358-2550. If NAM is unable or unwilling to perform
      its duties under this Agreement, the Parties shall mutually agree on an
      alternative administrator that will replace NAM and assume NAM's role
      consistent with this Agreement. If the Parties are unable to agree, they
      will petition a court of competent jurisdiction to appoint an
      administrator that will assume NAM's duties under this Agreement. Payment
      of all filing, administration and arbitrator fees will be governed by the
      NAM Rules or rules of the other organization.
    </p>

    <p>
      Notwithstanding any choice of law or other provision in this Agreement,
      the Parties agree and acknowledge that this Arbitration Clause evidences a
      transaction involving interstate commerce and that the Federal Arbitration
      Act, 9 U.S.C. § 1 et seq. ("FAA"), will govern its interpretation and
      enforcement and proceedings pursuant thereto. It is the intent of the
      parties that the FAA and NAM Rules pre-empt all state laws to the fullest
      extent permitted by law. If the FAA and NAM Rules are found to not apply
      to any issue that arises under this Arbitration Clause or the enforcement
      thereof, then that issue shall be resolved under the laws of the State of
      New York.
    </p>

    <h1>4. Arbitration Procedure.</h1>

    <p>
      A Party who desires to initiate arbitration must provide the other party
      with a written demand for arbitration as specified in the NAM Rules. The
      Parties agree that each individual claim for arbitration must set out the
      identity of the plaintiff and the plaintiff's counsel, a detailed
      description of the legal claims being asserted and the requested relief,
      including a good-faith calculation of the specific amount in dispute.
    </p>

    <p>
      The arbitrator, and not any federal, state, or local court or agency, has
      exclusive authority to resolve any disputes relating to the
      interpretation, applicability, enforceability or formation of this
      Arbitration Clause, including any claim that all or any part of this
      Arbitration Clause is void or voidable. The arbitrator is responsible for
      determining all threshold arbitrability issues, including issues relating
      to whether this Agreement is unconscionable or illusory and any defense to
      arbitration, including waiver, delay, laches, or estoppel.
    </p>

    <p>
      The arbitrator will conduct hearings, if any, by teleconference or
      videoconference, rather than by personal appearances, unless the
      arbitrator determines upon request by you or by us (or Program Manager)
      that an in-person hearing is required. Any in-person appearances will be
      held at a location which is reasonably convenient to the Parties with due
      consideration of their ability to travel and other pertinent
      circumstances. If the Parties are unable to agree on a location, such
      determination should be made by the NAM or by the arbitrator.
    </p>

    <p>
      The arbitrator's decision will follow the terms of this Agreement and will
      be final and binding, subject to any appeal rights under the FAA. The
      arbitrator must apply substantive governing law consistent with the FAA
      and applicable statutes of limitation and privileges. The arbitrator may
      award any damages or other relief or remedies that would apply under
      applicable law to an individual action brought in court. The arbitrator
      will have authority to award temporary, interim, or permanent injunctive
      relief or relief providing for specific performance of this Agreement, but
      only to the extent necessary to provide relief warranted by the individual
      claim before the arbitrator. The award rendered by the arbitrator may be
      confirmed and enforced in any court having jurisdiction thereof.
      Notwithstanding any of the foregoing, nothing in this Agreement will
      preclude you from bringing issues to the attention of federal, state, or
      local agencies and, if the law allows, they can seek relief against us (or
      the Program Manager) for you.
    </p>

    <h1>5. Mass Filing.</h1>

    <p>
      If, at any time, 25 or more similar demands for arbitration are asserted
      against us, Program Manager or related parties by the same or coordinated
      counsel or entities ("Mass Filing"), consistent with the definition and
      criteria of Mass Filings set forth in the NAM's Mass Filing Supplemental
      Dispute Resolution Rules and Procedures ("NAM's Mass Filing Rules",
      available at
      <a
        href="https://www.namadr.com/resources/rules-fees-forms/"
        target="_blank"
        rel="noopener noreferrer"
      >
        https://www.namadr.com/resources/rules-fees-forms/
      </a>
      ), the additional protocols set forth below shall apply.
    </p>

    <p>
      i. If you or your counsel file a demand for arbitration (a "Demand") that
      fits within the definition of Mass Filing referred to above, then you
      agree that your Demand for arbitration shall be subject to the additional
      protocols set forth in this Mass Filing subsection. You also acknowledge
      that the adjudication of your dispute might be delayed and that any
      applicable statute of limitations shall be tolled from the time at which
      you initiate informal dispute resolution until your Demand is decided,
      withdrawn, or settled.
    </p>

    <p>
      ii. NAM's Mass Filing Rules shall apply if your Demand is deemed by NAM,
      in its sole discretion pursuant to NAM's Mass Filing Rules and this
      dispute resolution section, to be part of a Mass Filing. Such election for
      NAM's Mass Filing Rules and related fee schedule must be made by either
      you, us, or Program Manager in writing and submitted to NAM and all
      Parties.
    </p>

    <p>
      iii. Bellwether Proceedings. Bellwether proceedings are encouraged by
      courts and arbitration administrators when there are multiple disputes
      involving similar claims against the same or related parties. Counsel for
      the Mass Filings claimants (including you) and counsel for us (or Program
      Manager) shall each select 15 Demands (30 total), and no more than 30
      arbitrations shall be filed, processed, adjudicated, or pending at the
      same time, with each of the 30 individual arbitrations presided over by a
      different arbitrator, in a first set of bellwether proceedings. During
      this time, no other Demands that are part of the Mass Filings may be
      filed, processed, adjudicated, or pending. If the Parties are unable to
      resolve the remaining Demands after the first set of bellwether
      proceedings are arbitrated or otherwise resolved, then counsel for the
      claimants and counsel for us (or Program Manager) shall each select an
      additional 15 Demands (30) total to be filed, processed, and adjudicated
      as individual arbitrations, with each of the 30 arbitrations presided over
      by a different arbitrator, in a second set of bellwether proceedings.
      During this time, no other Demands for arbitration that are part of the
      Mass Filings may be filed, processed, or adjudicated. This staged process
      of bellwether proceedings, with each set including 30 Demands adjudicated
      on an individual basis, shall continue until each Demand included in the
      Mass Filings (including your Demand) is adjudicated or otherwise resolved.
      Fees associated with a Demand included in the Mass Filings, including fees
      owed by the us or the Program Manager and the claimants (including you),
      shall only be due after your Demand is chosen as part of a set of
      bellwether proceedings and therefore properly designated for filing,
      processing, and adjudication. Any applicable statute of limitations shall
      be tolled beginning when you initiate the informal dispute resolution
      process set forth above in this section 5 of this Arbitration Clause, and
      if the first Mass Filings' Demands are chosen for the initial set of
      bellwether proceedings have been filed, your claims will remain tolled
      until your Demand is decided, withdrawn, or settled. A court of competent
      jurisdiction located in New York, New York, U.S.A. shall have the power to
      enforce this subsection.
    </p>

    <p>
      iv. The bellwether proceedings set forth in subsection (iii) above are
      preferred by the Parties. However, if said proceedings are determined to
      not be feasible under the circumstances, the Parties agree to cooperate
      with each other and the arbitrator to establish alternative processes or
      procedures that the arbitration provider or arbitrator believe will
      provide for an efficient, cost-effective resolution of claims. Any
      disagreement between the parties as to whether subsection (iii) above
      should apply shall be resolved by a procedural arbitrator appointed by
      NAM.
    </p>

    <p>
      v. The Parties each agree that we each value the integrity and efficiency
      of the arbitration and small claims court process and wish to employ the
      process for the fair resolution of genuine and sincere disputes between
      us. The Parties acknowledge and agree to act in good faith to ensure the
      fair resolution of genuine and sincere disputes. The Parties further agree
      that application of these Mass Filings procedures have been reasonably
      designed to result in an efficient and fair adjudication of such cases.
    </p>

    <h1>6. Class and Consolidated Action Waiver.</h1>

    <p>
      You agree that any arbitration under this Agreement will take place on an
      individual basis, and not as a class, collective, private attorney
      general, or representative action or proceeding and such class,
      collective, private attorney general, or representative arbitrations are
      not permitted, and (unless all parties otherwise agree in writing) you are
      agreeing to give up the ability to participate in or join claims of
      multiple individuals against us (or Program Manager) in a single
      proceeding (the "Class Action Waiver").
    </p>

    <p>
      If a claim does not proceed in arbitration for any reason, the Class
      Action Waiver will remain in effect, and you shall not join or file any
      action or proceeding in court on a class, representative or joint basis
      against us (or the Program Manager).
    </p>

    <h1>7. Jury Waiver.</h1>

    <p>
      Each Party waives the right to a trial by jury in any court in any suit,
      action, or proceeding on any matter arising in connection with or in any
      way related to the transactions of which this Agreement is a part or its
      enforcement, except where such waiver is prohibited by law or deemed by a
      court of law to be against public policy. The Parties each acknowledge
      that each Party makes this waiver knowingly, willingly and voluntarily and
      without duress, and only after extensive consideration of the
      ramifications of this waiver with their respective attorneys.
    </p>

    <h1>8. Small Claims Exception.</h1>

    <p>
      Notwithstanding the foregoing, either you, us, or Program Manager may
      elect to have an individual claim heard in small claims court. If the
      request to proceed in small claims court is made after an arbitration has
      been initiated but before an arbitrator has been appointed, such
      arbitration shall be administratively closed. Any controversy over the
      small claims court's jurisdiction shall be determined solely by the small
      claims court.
    </p>

    <p>
      All other issues (except as otherwise provided herein) are exclusively for
      the arbitrator to decide, as well as any request to proceed in small
      claims court that is made after an arbitrator has been appointed. If you,
      us, or the Program Manager challenges the small claims court election in
      your dispute, and the small claims court determines that it does not have
      jurisdiction, then the claim shall be heard in arbitration. However, such
      court determination shall not be considered or deemed binding with respect
      to Program Manager's other contracting parties.
    </p>

    <h1>9. Venue and Forum Selection.</h1>

    <p>
      Except where prohibited by law and except for claims that are heard in a
      small claims court as set forth in this Agreement, any claims arising out
      of or relating to this Agreement, our services, or to your relationship
      with us or Program Manager that for whatever reason are not required to be
      arbitrated or filed in small claims court, will be litigated exclusively
      in the federal or state courts located in New York, New York, U.S.A. You
      and Program Manager consent to the exercise of personal jurisdiction of
      courts in the State of New York and waive any claim that such courts
      constitute an inconvenient forum.
    </p>

    <h1>10. Severability; Survival; Conflicts.</h1>

    <p>
      If any part of this Arbitration Clause is found by a court or arbitrator
      to be unenforceable, the remainder is enforceable, except that: (A) if the
      Class Action Waiver is limited, voided or found unenforceable in a
      proceeding between you and us, and that determination becomes final after
      all appeals have been exhausted, then this Arbitration Clause (except for
      this sentence) shall be null and void with respect to such proceeding; and
      (B) if a claim is brought seeking public injunctive relief and a court
      determines that the restrictions in the Class Action Waiver or elsewhere
      in this Arbitration Clause prohibiting the arbitrator from awarding relief
      on behalf of third parties are unenforceable with respect to such claim,
      and that determination becomes final after all appeals have been
      exhausted, the claim for public injunctive relief will be determined in
      court and any individual claims seeking monetary relief will be
      arbitrated. In such a case the parties will request that the court stay
      the claim for public injunctive relief until the arbitration award
      pertaining to individual relief has been entered in court. This
      Arbitration Clause will survive the termination of the Agreement,
      regardless of reason for termination, the sale or assignment of your
      obligation by us to a third party, the repayment of some or all amounts
      owed under the Agreement and any bankruptcy by you, to the extent
      consistent with applicable bankruptcy law. In the event of a conflict or
      inconsistency between this Arbitration Clause, on the one hand, and the
      applicable arbitration rules or the other provisions of the Agreement, on
      the other hand, this Arbitration Clause shall govern.
    </p>

    <h1>BILLING RIGHTS SUMMARY</h1>

    <p>
      YOUR BILLING RIGHTS – KEEP THIS NOTICE FOR FUTURE USE: This notice tells
      you about your rights and our responsibilities under the Fair Credit
      Billing Act.
    </p>

    <p>WHAT TO DO IF YOU FIND A MISTAKE ON YOUR STATEMENT:</p>

    <p>If you think there is an error on your Statement, write to us at:</p>

    <p>
      Cloaked Pay, LLC
      <br />
      116 East 16th St, Floor 6, New York, NY, 10003
    </p>

    <p>
      You may also contact us on the Web and/or by email: {{ SUPPORT_EMAIL }}
    </p>

    <p>In your letter, give us the following information:</p>

    <p>Account information: Your name and Card Account number.</p>

    <p>Dollar amount: The dollar amount of the suspected error.</p>

    <p>
      Description of the problem: If you think there is an error on your bill,
      describe what you believe is wrong and why you believe it is a mistake.
    </p>

    <p>You must contact us:</p>

    <p>Within 60 days after the error appeared on your Statement.</p>

    <p>
      At least 3 business days before an automated payment is scheduled, if you
      want to stop payment on the amount you think is wrong.
    </p>

    <p>
      You must notify us of any potential errors in writing [or electronically].
      You may call us at +1 (888) 857-5490, but if you do, we are not required
      to investigate any potential errors, and you may have to pay the amount in
      question.
    </p>

    <p>WHAT WILL HAPPEN AFTER WE RECEIVE YOUR EMAIL:</p>

    <p>When we receive your letter, we must do two things:</p>

    <p>
      Within 30 days of receiving your letter, we must tell you that we received
      your letter. We will also tell you if we have already corrected the error;
      and
    </p>

    <p>
      Within 90 days of receiving your letter, we must either correct the error
      or explain to you why we believe the bill is correct.
    </p>

    <p>While we investigate whether or not there has been an error:</p>

    <p>
      We cannot try to collect the amount in question, or report you as
      delinquent on that amount;
    </p>

    <p>
      The charge in question may remain on your statement, and we may continue
      to charge you interest on that amount;
    </p>

    <p>
      While you do not have to pay the amount in question, you are responsible
      for the remainder of your balance.
    </p>

    <p>We can apply any unpaid amount against your Credit Limit.</p>

    <p>After we finish our investigation, one of two things will happen:</p>

    <p>
      If we made a mistake: You will not have to pay the amount in question or
      any interest or other fees related to that amount.
    </p>

    <p>
      If we believe there was a mistake: You will have to pay the amount in
      question, along with applicable interest and fees. We will send you a
      statement of the amount you owe and the date payment is due. We may then
      report you as delinquent if you do not pay the amount we think you owe.
    </p>

    <p>
      If you receive our explanation but still believe your bill is wrong, you
      must write to us within 10 days telling us that you still refuse to pay.
      If you do so, we cannot report you as delinquent without also reporting
      that you are questioning your bill. We must tell you the name of anyone to
      whom we reported you as delinquent, and we must let those organizations
      know when the matter has been settled between us.
    </p>

    <p>
      If we do not follow all the rules above, you do not have to pay the first
      $50 of the amount you question, even if your bill is correct.
    </p>

    <p>YOUR RIGHTS IF YOU ARE DISSATISFIED WITH YOUR CARD PURCHASE:</p>

    <p>
      If you are dissatisfied with the goods or services that you have purchased
      with your Card, and you have tried in good faith to correct the problem
      with the merchant, you may have the right not to pay the remaining amount
      due on the purchase.
    </p>

    <p>To use this right, all of the following must be true:</p>

    <p>
      1. The purchase must have been made in your home state or within 100 miles
      of your current mailing address, and the purchase price must have been
      more than $50. (Note: Neither these are necessary if your purchase was
      based on an advertisement we mailed to you, or if we own the company that
      sold you the goods or services.)
    </p>

    <p>
      2. You must have used your Card for the purchase. Purchases made with cash
      advances from an ATM or with a check that accesses your Card Account do
      not qualify.
    </p>

    <p>3. You must not yet have fully paid for the purchase.</p>

    <p>
      If all of the criteria above are met and you are still dissatisfied with
      the purchase, contact us in writing or electronically at:
    </p>

    <p>
      Cloaked Pay, LLC
      <br />
      116 East 16th Street, Floor 6, New York, 10003
    </p>

    <p>
      You may also contact us on the Web and/or by email: {{ SUPPORT_EMAIL }}
    </p>

    <p>
      While we investigate, the same rules apply to the disputed amount as
      discussed above. After we finish our investigation, we will tell you our
      decision. At that point, if we think you owe an amount and you do not pay,
      we may report you as delinquent.
    </p>
  </div>
</template>

<style scoped lang="scss">
/* stylelint-disable */
table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid $color-primary-100;

  thead {
    th {
      text-align: left;
      padding: 10px;
      border-bottom: 1px solid $color-primary-100;
      font-weight: 600;
    }
  }

  tbody {
    tr {
      td {
        padding: 10px;
        border-bottom: 1px solid $color-primary-100;
        border-left: 1px solid $color-primary-100;

        &:first-of-type {
          border-left: none;
        }

        ul {
          margin-top: 15px !important;
          margin-left: 15px;

          li {
            margin-top: 5px;
            position: relative;

            &::before {
              content: "";
              position: absolute;
              top: 50%;
              left: -15px;
              width: 5px;
              height: 5px;
              border-radius: 50%;
              background-color: $color-primary-100;
              transform: translateY(-50%);
            }

            &:first-child {
              margin-top: 0;
            }
          }
        }
      }
    }
  }
}

h1 {
  font-size: 14px;
  font-weight: 600;
}

* {
  margin-top: 20px;
  font-size: 12px;
  font-style: normal;
  line-height: 16.8px;
  font-weight: 500;

  &:first-child {
    margin-top: 0;
  }
}

div {
  a {
    color: $color-primary-100;
    text-decoration: underline;

    &:hover {
      color: $color-primary-70;
    }
  }
}
</style>
